import { useRBACPermissions } from '@/contexts/useRBACPermissions'

export type SalesforceLinkPermissions = {
  canRead: boolean
  /** Strictly narrower than `canRead`: a reader sees the card, not the buttons. */
  canWrite: boolean
  /**
   * Whether the roles query has settled. Until it does, both flags read
   * `false`, so a caller that only hides UI self-corrects when the roles
   * arrive — but one that acts irreversibly on a denial, such as redirecting,
   * must wait for this or it bounces deep links before the roles are fetched.
   * A failed query counts as settled: deny rather than hang on a skeleton.
   */
  isResolved: boolean
}

/**
 * Mirrors the backend guards: reads `{ADMIN, OPERATOR, READER}`, writes
 * `{ADMIN, OPERATOR}`.
 *
 * `gtm` is deliberately absent, not a missing case. A gtm person reaches this
 * surface through the `reader` baseline every internal user holds (APP-1335),
 * so adding `hasGtmRights()` here would widen the guard to paper over a person
 * whose roles are wrong. Widen the person's roles, never the guard.
 */
export function useCanManageSalesforceLink(): SalesforceLinkPermissions {
  const { hasAdminRights, hasOperatorRights, hasReaderRights, userRoles } =
    useRBACPermissions()

  const isAdmin = hasAdminRights()
  const isOperator = hasOperatorRights()

  return {
    canRead: isAdmin || isOperator || hasReaderRights(),
    canWrite: isAdmin || isOperator,
    isResolved: userRoles.isSuccess || userRoles.isError,
  }
}
